import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { companiesAPI, odooConfigAPI } from '../services/api.ts';
import {
  Alert,
  Button,
  ColorInput,
  Divider,
  FileInput,
  Group,
  PasswordInput,
  Select,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { uploadToCloudinary } from '../services/index.tsx';

interface ProjectFormProps {
  initialValues: {
    _id?: string;
    name: string;
    status?: number;
    company_id?: string | number | { id: number; name: string };
    logo?: File | string;
    color_scheme?: {
      primary: string;
      secondary: string;
    };
    odoo?: {
      url: string;
      db: string;
      username: string;
      password: string;
    };
    odoo_configuration_id?: number;
    admin_email: string;
    admin_password?: string;
    heading?: string;
    sub_heading?: string;
    audit_department_email?: string;
  };
  onSubmit: (formData: any) => Promise<void>;
  onCancel: () => void;
}

const ProjectSchema = Yup.object().shape({
  name: Yup.string()
    .min(3, 'Too Short!')
    .max(50, 'Too Long!')
    .required('Required'),
  status: Yup.number().oneOf([0, 1], 'Invalid status').default(1),
  odoo_configuration_id: Yup.number().required(
    'Odoo configuration is required',
  ),
  company_id: Yup.number().required('Company is required'),
  color_scheme: Yup.object().shape({
    primary: Yup.string(),
    secondary: Yup.string(),
  }),
  admin_email: Yup.string().email('Invalid email').required('Required'),
  admin_password: Yup.string().when('_id', {
    is: (val: string | undefined) => !val,
    then: (schema) =>
      schema
        .min(8, 'Password must be at least 8 characters')
        .required('Required for new project'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export const ProjectForm: React.FC<ProjectFormProps> = ({
  initialValues,
  onSubmit,
  onCancel,
}) => {
  const [companies, setCompanies] = useState<any[]>([]);
  const [odooConfigurations, setOdooConfigurations] = useState<any[]>([]);
  const [selectedOdooId, setSelectedOdooId] = useState<number | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  // Find matching Odoo configuration based on URL
  const findOdooConfigId = (configs: any[], username?: string) => {
    if (!username) return null;
    const config = configs.find((c) => c.username === username);
    return config ? config.id : null;
  };

  // Fetch Odoo configurations and set initial values
  useEffect(() => {
    const fetchOdooConfigs = async () => {
      try {
        const response = await odooConfigAPI.getAll();
        setOdooConfigurations(response?.data?.configurations || []);

        // Find matching Odoo configuration based on URL
        if (initialValues.odoo?.username) {
          const matchingConfigId = findOdooConfigId(
            response?.data?.configurations as any[],
            initialValues.odoo.username,
          );

          if (matchingConfigId) {
            setSelectedOdooId(matchingConfigId);
          }
        }
      } catch (error) {
        console.error('Failed to fetch Odoo configurations:', error);
        setApiError('Failed to load Odoo configurations');
      }
    };
    fetchOdooConfigs();

    // eslint-disable-next-line
  }, [initialValues.odoo?.url]);

  // Fetch companies when Odoo configuration is selected
  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        if (selectedOdooId) {
          const response = await companiesAPI.getAll(selectedOdooId);
          if (response.success) {
            setCompanies(response.data.companies);
          }
        }
      } catch (error) {
        console.error('Failed to fetch companies:', error);
        setApiError('Failed to load companies');
      } finally {
        setLoading(false);
      }
    };

    if (selectedOdooId) {
      fetchCompanies();
    }
  }, [selectedOdooId]);

  // Update form value when selectedOdooId changes
  const formikRef = useRef<any>(null);
  useEffect(() => {
    if (selectedOdooId && formikRef.current) {
      formikRef.current.setFieldValue('odoo_configuration_id', selectedOdooId);
    }
  }, [selectedOdooId]);

  return (
    <Formik
      innerRef={formikRef}
      initialValues={{
        ...initialValues,
        heading: initialValues.heading || '',
        sub_heading: initialValues.sub_heading || '',
        company_id:
          typeof initialValues.company_id === 'object'
            ? (initialValues.company_id as { id: number }).id
            : typeof initialValues.company_id === 'number'
              ? initialValues.company_id
              : Number(initialValues.company_id) || '',
        odoo_configuration_id: '',
        color_scheme: {
          primary: initialValues.color_scheme?.primary || '#000000',
          secondary: initialValues.color_scheme?.secondary || '#000000',
        },
        audit_department_email: initialValues.audit_department_email || '',
      }}
      validationSchema={ProjectSchema}
      onSubmit={async (
        values,
        {
          setSubmitting,
          setErrors,
        }: {
          setSubmitting: (isSubmitting: boolean) => void;
          setErrors: (errors: { submit?: string }) => void;
        },
      ) => {
        try {
          let logoUrl = values.logo;

          // If logo is a File, upload it to Cloudinary first
          if (values.logo instanceof File) {
            try {
              logoUrl = await uploadToCloudinary(values.logo, 'projects');
            } catch (uploadError: any) {
              console.error('Cloudinary upload error:', uploadError);
              throw new Error('Failed to upload logo to Cloudinary');
            }
          }

          const projectData = {
            name: values.name,
            company_id: values.company_id,
            odoo_configuration_id: values.odoo_configuration_id,
            admin_email: values.admin_email,
            audit_department_email: values.audit_department_email,
            admin_password: values.admin_password,
            color_scheme: values.color_scheme,
            logo: logoUrl,
            status: values.status,
            heading: values.heading,
            sub_heading: values.sub_heading,
          };

          // Remove undefined or empty password on update
          if (!projectData.admin_password) {
            delete projectData.admin_password;
          }

          await onSubmit(projectData);
        } catch (error: any) {
          console.error('Form submission error:', error);
          setErrors({
            submit:
              error.response?.data?.message ||
              error.message ||
              'An error occurred during submission',
          });
        } finally {
          setSubmitting(false);
        }
      }}
    >
      {({
        setFieldValue,
        setFieldTouched,
        getFieldProps,
        isSubmitting,
        errors,
        touched,
        values,
      }: any) => {
        return (
          <Form>
            <Stack gap="xl">
              {/* Basic Information Section */}
              <Stack gap="md">
                <Title order={5}>Basic Information</Title>
                <Divider />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <TextInput
                    label="Project Name"
                    withAsterisk
                    {...getFieldProps('name')}
                    error={touched.name && errors.name}
                  />

                  <Select
                    label="Odoo Configuration"
                    withAsterisk
                    placeholder="Select Odoo Configuration"
                    data={odooConfigurations.map((config) => ({
                      value: String(config.id),
                      label: String(config.name ?? ''),
                    }))}
                    value={
                      values.odoo_configuration_id
                        ? String(values.odoo_configuration_id)
                        : null
                    }
                    onChange={(selected) => {
                      const value = Number(selected || '');
                      setSelectedOdooId(value);
                      setFieldValue('odoo_configuration_id', value);
                      setFieldValue('company_id', '');
                    }}
                    onBlur={() => setFieldTouched('odoo_configuration_id', true)}
                    disabled={loading}
                    error={
                      touched.odoo_configuration_id &&
                      errors.odoo_configuration_id
                    }
                  />

                  <Stack gap={4}>
                    <Select
                      label="Company"
                      withAsterisk
                      placeholder="Select a company"
                      data={companies.map((company) => ({
                        value: String(company.id),
                        label: String(company.name ?? ''),
                      }))}
                      value={
                        values.company_id === '' || values.company_id == null
                          ? null
                          : String(values.company_id)
                      }
                      onChange={(selected) =>
                        setFieldValue('company_id', selected ?? '')
                      }
                      onBlur={() => setFieldTouched('company_id', true)}
                      disabled={!selectedOdooId || loading}
                      error={touched.company_id && errors.company_id}
                    />
                    {apiError && (
                      <Text size="sm" c="red">
                        {apiError}
                      </Text>
                    )}
                  </Stack>

                  <FileInput
                    label="Logo"
                    placeholder="Choose file"
                    accept="image/png"
                    value={values.logo instanceof File ? values.logo : null}
                    onChange={(file) => {
                      if (file) {
                        setFieldValue('logo', file);
                      }
                    }}
                  />

                  <TextInput label="Heading" {...getFieldProps('heading')} />

                  <TextInput
                    label="Sub Heading"
                    {...getFieldProps('sub_heading')}
                  />
                </SimpleGrid>
              </Stack>

              {/* Color Scheme Section */}
              <Stack gap="md">
                <Title order={5}>Color Scheme</Title>
                <Divider />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <ColorInput
                    label="Primary Color"
                    name="color_scheme.primary"
                    fixOnBlur={false}
                    value={values.color_scheme?.primary ?? ''}
                    onChange={(value) =>
                      setFieldValue('color_scheme.primary', value)
                    }
                  />
                  <ColorInput
                    label="Secondary Color"
                    name="color_scheme.secondary"
                    fixOnBlur={false}
                    value={values.color_scheme?.secondary ?? ''}
                    onChange={(value) =>
                      setFieldValue('color_scheme.secondary', value)
                    }
                  />
                </SimpleGrid>
              </Stack>

              {/* Admin Configuration Section */}
              <Stack gap="md">
                <Title order={5}>Admin Configuration</Title>
                <Divider />
                <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="md">
                  <TextInput
                    label="Admin Email"
                    withAsterisk
                    type="email"
                    {...getFieldProps('admin_email')}
                    error={touched.admin_email && errors.admin_email}
                  />

                  <PasswordInput
                    label="Admin Password"
                    withAsterisk={!initialValues._id}
                    placeholder={
                      initialValues._id
                        ? 'Leave blank to keep the same password'
                        : ''
                    }
                    visible={showPassword}
                    onVisibilityChange={setShowPassword}
                    {...getFieldProps('admin_password')}
                    value={values.admin_password ?? ''}
                    error={touched.admin_password && errors.admin_password}
                  />
                </SimpleGrid>

                <TextInput
                  label="Audit Department Email"
                  {...getFieldProps('audit_department_email')}
                />
              </Stack>

              {errors?.submit && (
                <Alert color="red" variant="light">
                  {errors.submit}
                </Alert>
              )}

              {/* Form Actions */}
              <Group justify="flex-end" gap="sm">
                <Button variant="default" onClick={onCancel}>
                  Cancel
                </Button>
                <Button type="submit" loading={isSubmitting}>
                  {initialValues._id ? 'Update' : 'Create'}
                </Button>
              </Group>
            </Stack>
          </Form>
        );
      }}
    </Formik>
  );
};
